import React, { useState } from 'react';
import {
  PencilSimple,
  Trash,
  CaretDown,
  CaretRight,
  Package,
  Tag,
  ListChecks,
  CheckCircle,
  XCircle,
} from '@phosphor-icons/react';
import { AssetCategory, FieldType } from 'shared/types/asset';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface AssetCategoryListProps {
  categories: AssetCategory[];
  onEdit: (category: AssetCategory) => void;
  onDeactivate: (category: AssetCategory) => void;
  isLoading?: boolean;
}

export const AssetCategoryList: React.FC<AssetCategoryListProps> = ({
  categories,
  onEdit,
  onDeactivate,
  isLoading = false,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const getFieldTypeLabel = (fieldType: FieldType): string => {
    const labels: Record<FieldType, string> = {
      [FieldType.STRING]: 'Tekst',
      [FieldType.NUMBER]: 'Getal',
      [FieldType.BOOLEAN]: 'Ja/Nee',
      [FieldType.DATE]: 'Datum',
      [FieldType.ENUM]: 'Dropdown',
      [FieldType.MULTI_ENUM]: 'Meerkeuze',
      [FieldType.FILE_REFERENCE]: 'Bestand',
      [FieldType.COMPANY_REFERENCE]: 'Bedrijf (CRM)',
    };
    return labels[fieldType] || fieldType;
  };

  const toggleExpanded = (categoryId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const sortedCategories = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-8 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
            <Package className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-lg mb-2">Nog geen categorieën</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Maak je eerste voorziening categorie aan om te beginnen met het beheren van voorzieningen
            in je vastgoedportefeuille.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {sortedCategories.map((category) => {
        const isExpanded = expandedIds.has(category.id);
        const hasTypes = (category.assetTypes?.length || 0) > 0;
        const hasFields = (category.fieldDefinitions?.length || 0) > 0;
        const hasExpandableContent = hasTypes || hasFields;

        return (
          <Card key={category.id} className={!category.active ? 'opacity-60' : ''}>
            <CardContent className="p-0">
              {/* Main Row */}
              <div className="flex items-center gap-4 p-4">
                {/* Expand trigger */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 shrink-0 ${!hasExpandableContent ? 'invisible' : ''}`}
                  onClick={() => hasExpandableContent && toggleExpanded(category.id)}
                >
                  {isExpanded ? (
                    <CaretDown className="h-4 w-4" />
                  ) : (
                    <CaretRight className="h-4 w-4" />
                  )}
                </Button>

                {/* Category icon */}
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Package className="h-5 w-5 text-primary" />
                </div>

                {/* Category info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium truncate">{category.name}</h4>
                    {category.active ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Actief
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inactief
                      </Badge>
                    )}
                  </div>
                  {category.description && (
                    <p className="text-sm text-muted-foreground truncate mt-0.5">
                      {category.description}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 shrink-0">
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Tag className="h-4 w-4" />
                    <span className="font-medium">{category.assetTypes?.length || 0}</span>
                    <span className="hidden sm:inline">types</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <ListChecks className="h-4 w-4" />
                    <span className="font-medium">{category.fieldDefinitions?.length || 0}</span>
                    <span className="hidden sm:inline">velden</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(category);
                    }}
                    title="Bewerken"
                  >
                    <PencilSimple className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeactivate(category);
                    }}
                    title={category.active ? 'Deactiveren' : 'Activeren'}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && hasExpandableContent && (
                <div className="px-4 pb-4 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-12 border-t pt-4">
                    {/* Types */}
                    {hasTypes && (
                      <div>
                        <h5 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                          <Tag className="h-4 w-4" />
                          Types ({category.assetTypes?.length || 0})
                        </h5>
                        <div className="space-y-1.5">
                          {category.assetTypes?.map((type) => (
                            <div
                              key={type.id}
                              className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                            >
                              <div>
                                <span className="text-sm font-medium">{type.name}</span>
                                {type.description && (
                                  <p className="text-xs text-muted-foreground">{type.description}</p>
                                )}
                              </div>
                              <div className="flex gap-1">
                                {type.scopes?.map((s) => (
                                  <Badge key={s} variant="outline" className="text-xs">
                                    {s}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fields */}
                    {hasFields && (
                      <div>
                        <h5 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                          <ListChecks className="h-4 w-4" />
                          Velden ({category.fieldDefinitions?.length || 0})
                        </h5>
                        <div className="space-y-1.5">
                          {category.fieldDefinitions?.map((field) => (
                            <div
                              key={field.id}
                              className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{field.displayName}</span>
                                {field.isRequired && (
                                  <span className="text-xs text-destructive">*</span>
                                )}
                              </div>
                              <Badge variant="secondary" className="text-xs">
                                {getFieldTypeLabel(field.fieldType)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
